import React, {Component, useMemo, useState} from "react";
import NotificationManager from "react-notifications/lib/NotificationManager";
import {AgGridReact} from "ag-grid-react";
import {
    ColumnMenuModule,
    ColumnsToolPanelModule,
    ContextMenuModule,
    CsvExportModule,
    ExcelExportModule,
    SetFilterModule,
} from "ag-grid-enterprise";
import {AllCommunityModule, ClientSideRowModelModule} from "ag-grid-community";
import {Helmet} from "react-helmet";
import {defaultZenGroupColumnInitWithOptionsWithValueGetter} from "../../utils/zenGroupDisplayNameGridHelper";
import {dateValueFormatter} from "../../utils/gridDateFormatter";
import {ClearRefresh} from "../../components/clearRefreshButtons";
import CustomNameCellEditor from "../../utils/customCellEditor";
import {handleGroupColumnChangeNameOnly} from "../../utils/gridCellEditing";
import DTPicker, {dateFilterParametersInHeaderClientSideGrid} from "../../utils/DTPicker";
import {
    loadDataWithSSEAndStartChangeStreamListener,
    standardHandleInsertEvent,
    standardHandlePopulateGrid,
    standardHandleUpdateAndReplaceEvent
} from "../../utils/sseAndChangeStreamHelper";
import privatePageHeaderHelper from "../../utils/privatePageHeaderHelper";
import {standardExcelExportHelper, standardExcelExportObjectInContextMenu} from "../../utils/excelExportHelper";
import {
    getColumnModeInSession,
    getDefaultAgGridSidebarProps,
    getUseColumnStateInSession,
    getUseFilterStateInSession,
    onColumnStateChangedHelper,
    onFilterChangedHelper,
    onGridReadyHelper,
    onGridReadyHelperForColumnState,
    updateColumnModeInSessionHelper,
    updateUseColumnStateHelper,
    updateUseFilterStateHelper
} from "../../utils/gridFilterStateAndColumnStateHelper";
import {GridColumnFilterStateSaving} from "../../components/columnfilterComponent";
import {
    ClickToShowColumnOptionsWithToggleButtonGroup,
    customColumnModeText, mediumColumnModeText,
    minColumnModeText,
    standardApplyMinimumOrMediumColumnMode
} from "../../components/clickToShowButtons";
import {
    approveToReinitializeForDetectedClonedAgentReactive,
    updateCustomerDeploymentHealthGridColumnModeReactive,
    updateCustomerDeploymentHealthGridColumnStateReactive,
    updateCustomerDeploymentHealthGridFilterModelReactive,
    updateCustomerDeploymentHealthGridUseColumnStateReactive,
    updateCustomerDeploymentHealthGridUseFilterStateReactive
} from "../api/customerDeploymentHealthApi";
import {defaultClientSideTextFilterParams} from "../../utils/filterHelper";
import {DisplayOptions} from "../../components/displayOptionsComponent";
import {Button, IconButton, ThemeProvider, Tooltip} from "@mui/material";
import {buttonTheme, cellButtonTheme, roundButtonTheme, switchTheme} from "../../utils/muiStyling";

import {useOutletContext} from "react-router-dom";
import {MuiIconButtonWithTooltip, MuiIconButtonWithTooltipAndBox} from "../../components/muiComponents";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import {checkPermission} from "../../utils/permissionCheckHelper";
import {releaseTrainingLicenseReactive} from "../api/trainingLicensesApi";

let gridColumnStateSessionVariableName = "customerDeploymentHealthGridColumnState"
let gridFilterStateSessionVariableName = "customerDeploymentHealthGridFilterState"
let minColumnIds = ["zenGroupDisplayName", "machineName", "installationResult", "humanReadableError", "dateCollected"]
let medColumnIds = ["zenGroupDisplayName", "machineName", "installationResult", "humanReadableError", "dateCollected", "originalClonedMachineName", "approvedToReinitialize", "resolvedAgentName"]
export default function CustomerDeploymentHealth() {
    const { isLoading, setIsLoading } = useOutletContext();
    const [gridApi, setGridApi] = useState(null);
    const [enableButtons, setEnableButtons] = useState(false);
    const [useFilterStateSettingToggled, setUseFilterStateSettingToggled] = useState(getUseFilterStateInSession(gridFilterStateSessionVariableName));
    const [useColumnStateSettingToggled, setUseColumnStateSettingToggled] = useState(getUseColumnStateInSession(gridColumnStateSessionVariableName));
    const [columnMode, setColumnMode] = useState(getColumnModeInSession(gridColumnStateSessionVariableName));
    const [sseDataPullActive, setSSEDataPullActive] = useState(true);
    const [asyncTransactionWaitMillis, setAsyncTransactionWaitMillis] = useState(200); //200 to start for the initial sse data pull, will change when sse data pull is done for change streams
    // eslint-disable-next-line no-unused-vars
    const [columnDefs, setColumnDefs] = useState([
        defaultZenGroupColumnInitWithOptionsWithValueGetter(true, true, true),
        { field: "zenGroupId", hide: true, suppressColumnsToolPanel: true, lockVisible: true},
        { field: "machineName", headerName: "Machine Name", initialWidth: 225,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false,
        },
        {
            field: "installationResult", headerName: "Installation Result", initialWidth: 230, sortable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                values: ["Clone Detected", "Failure"],
                suppressSorting: true,
                convertValuesToStrings: true,
            },
            valueGetter: (params) => {
                //Only CloneDetected needs formatted
                if (params.node.data.installationResult === "CloneDetected") return "Clone Detected"
                return params.node.data.installationResult
            }
        },
        { field: "humanReadableError", headerName: "Error", initialWidth: 400,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false,
            valueGetter: (params) => {
                if(params.node.data.humanReadableError){
                    return params.node.data.humanReadableError
                }
                else{
                    return "Unknown Error"
                }
            },
        },
        { field: "dateCollected", headerName: "Date Collected", initialWidth: 350,
            filter: 'agDateColumnFilter',
            sortable: true,
            filterParams: dateFilterParametersInHeaderClientSideGrid,
            valueFormatter: dateValueFormatter
        },
        { field: "installerVersion", headerName: "Installer Version", initialWidth: 250,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false,
        },
        { field: "osVersion", headerName: "OS Version", initialWidth: 250,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false,
        },
        { field: "fqdn", headerName: "FQDN", initialWidth: 250,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false,
        },
        {
            field: "approvedToReinitialize", headerName: "Cloned Agent Reinitialization Status", initialWidth: 350, sortable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                values: ["Resolved", "Reinitialization Pending", "Reinitialization Not Requested", "N/A"],
                suppressSorting: false,
                convertValuesToStrings: true,
                showTooltips: true
            },
            valueGetter: (params) => {
                if(params.node.data.installationResult !== "CloneDetected"){
                    return "N/A"
                }
                else{
                    if(params.node.data.approvedToReinitialize){
                        //Check if we have the resolved agent id or not
                        if(params.node.data.resolvedAgentName !== null){
                            //agent was found for the windowsOfflineInstallationId and windowsMachineSID for this clone
                            return "Resolved"
                        }
                        else {
                            //no agent found for this clone
                            return "Reinitialization Pending"
                        }
                    }
                    else{
                        //approvedToReinitialize is false, user has not clicked button to request the Reinitialization
                        return "Reinitialization Not Requested"
                    }
                }
            },
            cellRenderer: function (params) {
                let customElement = ""
                if(params.value === "Reinitialization Not Requested"){
                    customElement =
                        <MuiIconButtonWithTooltip
                            icon={
                                <FontAwesomeIcon
                                    className="object-contain"
                                    icon={ "fa-solid fa-triangle-exclamation" }
                                    color={"#FFD43B"}
                                    size={"1x"}
                                />
                            }
                            tooltipTitle={"A clone has been detected. Click to reinitialize this cloned agent. WARNING: This action cannot be reversed."}
                            tooltipPlacement={"bottom-start"}
                            onClick={(event) => {
                                if (!params.node.data.id) {
                                    NotificationManager.error(`Unexpected error making this request`)
                                    return
                                }

                                approveToReinitializeForDetectedClonedAgentReactive(params.node.data.id).then(() => {
                                    NotificationManager.success(`Successfully sent request`)

                                }).catch(function (error) {
                                    if (error.message) {
                                        NotificationManager.error(error.message)
                                    } else {
                                        NotificationManager.error(`Unexpected error making this request`)
                                    }
                                })
                            }}
                        />

                }
                else if(params.value === "Resolved"){
                    customElement =
                        <MuiIconButtonWithTooltip
                            icon={<FontAwesomeIcon className="object-contain" icon="fa-solid fa-check" size="xs" color="green" />}
                            tooltipTitle={"This agent has been successfully reinitialized"}
                            tooltipPlacement={"bottom-start"}
                            onClick={(event) => {}}
                        />
                }
                else if (params.value === "Reinitialization Pending"){
                    customElement =
                        <MuiIconButtonWithTooltip
                            icon={
                                <FontAwesomeIcon
                                    className={`object-contain`}
                                    icon={"fa-duotone fa-calendar-clock"}
                                    size="xs"
                                />
                            }
                            tooltipTitle={"A user has already requested for this cloned agent to be reinitialized and is still pending completion"}
                            tooltipPlacement={"bottom-start"}
                            onClick={(event) => {}}
                        />
                }
                return (
                    <div className={`flex flex-row items-center gap-x-1`}>
                        {customElement}
                        {params.value}
                    </div>
                )
            },
        },
        { field: "windowsOfflineInstallationId", headerName: "Cloned Agent Windows Offline Installation ID", initialWidth: 425,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false,
        },
        { field: "windowsMachineSID", headerName: "Cloned Agent Windows Machine SID", initialWidth: 340,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false,
        },
        { field: "resolvedAgentName", headerName: "Reinitialized Agent Name", initialWidth: 330,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false,
        },
        { field: "originalClonedAgentName", headerName: "Original Agent Name", initialWidth: 330,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false,
        },
        { field: "originalClonedMachineName", headerName: "Original Machine Name", initialWidth: 330,
            filter: 'agTextColumnFilter',
            filterParams: defaultClientSideTextFilterParams,
            sortable: true,
            editable: false,
        },
    ])
    const [defaultColDef, setDefaultColDef] = useState(
        {
            resizable: true,
            filterParams: null,
            floatingFilter: true,
            headerClass: "border-0 border-b-0",
            cellClass: "outline:none",
            enableCellChangeFlash: true,
            autoHeight: false,
            cellDataType: false //disable inferring cell data type automatically, can be overridden in individual colDef
        }
    )
    const sideBar = useMemo(() => {
        //Inside useMemo to help prevent the sidebar from re-rendering
        return getDefaultAgGridSidebarProps()
    }, []);
    const rowSelection = useMemo(() => {
        return {
            mode: 'multiRow',
            enableClickSelection: true,
            checkboxes: false,
            headerCheckbox: false
        };
    }, []);

    return (
        <div className="flex flex-col h-full">
            <Helmet>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>Customer Deployment Health</title>
                <script src="https://js.stripe.com/v3/"/>
                <link href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap" rel="stylesheet"/>
            </Helmet>
            <div className="flex flex-1 flex-col flex-nowrap gap-y-3 h-full">
                    {privatePageHeaderHelper("Customer Deployment Health")}
                    <hr className="h-0.5"/>
                    <DisplayOptions useFilterStateSettingToggled={useFilterStateSettingToggled}
                                    setUseFilterStateSettingToggled={setUseFilterStateSettingToggled}
                                    toggleUpdateUseFilterState={toggleUpdateUseFilterState}
                                    useColumnStateSettingToggled={useColumnStateSettingToggled}
                                    setUseColumnStateSettingToggled={setUseColumnStateSettingToggled}
                                    toggleUpdateUseColumnState={toggleUpdateUseColumnState} />
                    <div className="flex flex-row justify-between gap-x-1 gap-y-3">
                        <div className={"self-end flex flex-col gap-y-3"}>
                            <div className="flex flex-row justify-start gap-x-6 flex-wrap gap-y-2">
                                <MuiIconButtonWithTooltipAndBox
                                    icon={
                                        <IconButton
                                            disabled={!enableButtons} sx={{width: 25, height: 25}}
                                            className={`self-center object-contain`} disableRipple={true}
                                        >
                                            <FontAwesomeIcon
                                                className="object-contain"
                                                icon={ "fa-solid fa-triangle-exclamation" }
                                                color={`${!enableButtons ? "#C1c1c1" : "#FFD43B"}`}
                                                size={"sm"}
                                            />
                                        </IconButton>
                                    }
                                    tooltipTitle={"Click to reinitialize all selected cloned agents. WARNING: This action cannot be reversed."}
                                    tooltipPlacement={"top"} disabled={!enableButtons}
                                    onClick={() => {
                                        if(gridApi && gridApi.getSelectedNodes() && gridApi.getSelectedNodes().length > 0){
                                            let validIdsList = []
                                            gridApi.getSelectedNodes().forEach(currentInstallationReport => {
                                                if(currentInstallationReport.data.id && currentInstallationReport.data.installationResult === "CloneDetected") {
                                                    validIdsList.push(currentInstallationReport.data.id)

                                                    approveToReinitializeForDetectedClonedAgentReactive(currentInstallationReport.data.id).then(() => {}).catch(function (error) {
                                                        if (error.message) {
                                                            console.log(error.message)
                                                        }
                                                    })
                                                }
                                            })

                                            if(validIdsList.length < 1){
                                                NotificationManager.info(`None of the selected customer deployments were for the Installation Result of 'Clone Detected'`);
                                            }
                                            else{
                                                NotificationManager.success("Queued the reinitialization of eligible customer deployments where a cloned agent was detected for groups you have permission to do so in");
                                            }
                                        }
                                    }}/>
                            </div>
                        </div>
                        <div className={"flex flex-row flex-wrap gap-y-3 gap-x-8 self-end justify-end"}>
                            <ClickToShowColumnOptionsWithToggleButtonGroup
                                columnMode={columnMode} setColumnMode={setColumnMode}
                                gridColumnStateSessionVariableName={gridColumnStateSessionVariableName}
                                gridApi={gridApi} minColumnIds={minColumnIds} medColumnIds={medColumnIds} disableMediumOption={false}
                                updateGridColumnModeFunction={updateCustomerDeploymentHealthGridColumnModeReactive}/>
                            <ClearRefresh gridApi={gridApi} showRefreshIcon={false}
                                          sseDataPullActive={sseDataPullActive}
                                          showExcelExportIcon={true} excelExportFunction={excelExport}/>
                        </div>
                    </div>
                    <div className="h-full flex flex-col gap-y-5" id="gridRoot">
                        {getGrid()}
                    </div>
                </div>
        </div>
    );

    function getGrid() {
        return (
            <Grid
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                sideBar={sideBar}
                rowSelection={rowSelection}
                setGridApi={setGridApi}
                setEnableButtons={setEnableButtons}
                sseDataPullActive={sseDataPullActive}
                setSSEDataPullActive={setSSEDataPullActive}
                asyncTransactionWaitMillis={asyncTransactionWaitMillis}
                setAsyncTransactionWaitMillis={setAsyncTransactionWaitMillis}
                excelExport={excelExport}
                columnMode={columnMode}
                setColumnMode={setColumnMode}
            />
        );
    }

    function excelExport() {
        standardExcelExportHelper(gridApi, sseDataPullActive, "customerDeploymentHealthGridExport")
    }

    function toggleUpdateUseFilterState(toggleSetting){
        updateUseFilterStateHelper(toggleSetting, gridFilterStateSessionVariableName, updateCustomerDeploymentHealthGridUseFilterStateReactive);
    }
    function toggleUpdateUseColumnState(toggleSetting){
        updateUseColumnStateHelper(toggleSetting, gridColumnStateSessionVariableName, updateCustomerDeploymentHealthGridUseColumnStateReactive);
    }
}

class Grid extends Component {
    rowData = []
    updateTransactionsToApply = []
    abortController = new AbortController()

    constructor(props, onClickRow, filterVals) {
        super(props);
    }

    onFirstDataRendered = (params) => {

    };
    onColumnStateChanged = (params) => {
        //function to handle when column state changes: sort change, column visibility changes, or a column position on grid is moved
        if(params.source !== "api" && params.source !== "gridOptionsChanged"){
            this.props.setColumnMode && this.props.setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, updateCustomerDeploymentHealthGridColumnStateReactive)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, updateCustomerDeploymentHealthGridColumnModeReactive, customColumnModeText)
        }
        else if(params.source === "api" && params.type === "sortChanged"){
            this.props.setColumnMode && this.props.setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, updateCustomerDeploymentHealthGridColumnStateReactive)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, updateCustomerDeploymentHealthGridColumnModeReactive, customColumnModeText)
        }

    }
    onCellEditingStopped = (event) => {
        let gridApi = event.api
        if (event.column.colId === "zenGroupDisplayName") {
            handleGroupColumnChangeNameOnly(event)
        }

    }

    componentWillUnmount() {
        this.abortController.abort()
    }

    populateGrid = async (rowData) => {
        standardHandlePopulateGrid(rowData, this.gridApi)
    }

    updateGridForChangeStream = async (changeStreamData) => {
        let operationType = changeStreamData.operationType
        let objectBody = changeStreamData.body
        if(operationType === "UPDATE" || operationType === "REPLACE"){
            standardHandleUpdateAndReplaceEvent(objectBody, this.gridApi, this.props.sseDataPullActive, this.updateTransactionsToApply)
        }
        else if (operationType === "INSERT"){
            standardHandleInsertEvent(objectBody, this.gridApi, this.props.sseDataPullActive)
        }
    }

    getRowId = (params) => {
        return params.data.id
    }

    getContextMenuItems = (params) => {
        let excelExport = this.props.excelExport //don't have access to this.props below in the action function so define it here
        return [
            standardExcelExportObjectInContextMenu(excelExport),
            "resetColumns",
            "autoSizeAll"
        ];
    };

    onGridReady = async (gridReadyParams) => {
        this.gridApi = gridReadyParams.api;
        this.props.setGridApi(gridReadyParams.api);

        let columnMode = this.props.columnMode
        //check which initial column mode to apply
        if(columnMode === customColumnModeText){
            onGridReadyHelperForColumnState(gridReadyParams, gridColumnStateSessionVariableName)
        }
        else if(columnMode === minColumnModeText){
            standardApplyMinimumOrMediumColumnMode(gridColumnStateSessionVariableName, gridReadyParams.api, this.props.setColumnMode, minColumnModeText, minColumnIds, updateCustomerDeploymentHealthGridColumnModeReactive)
        }
        else if(columnMode === mediumColumnModeText){
            standardApplyMinimumOrMediumColumnMode(gridColumnStateSessionVariableName, gridReadyParams.api, this.props.setColumnMode, mediumColumnModeText, medColumnIds, updateCustomerDeploymentHealthGridColumnModeReactive)
        }
        //else if columnMode is max then the default column state already shows the max amount of columns no need to update

        onGridReadyHelper(gridReadyParams, gridFilterStateSessionVariableName);

        await loadDataWithSSEAndStartChangeStreamListener("/sse/customerDeploymentHealthListReactive", "/sse/listenToAgentInstallationReportEvent",
            this.populateGrid, this.updateGridForChangeStream, gridReadyParams, this.props.setSSEDataPullActive, this.props.setAsyncTransactionWaitMillis, this.updateTransactionsToApply,
            this.abortController)
        //gridReadyParams.api.sizeColumnsToFit()
    };
    render() {
        return (
            <div className={"w-full h-full"} style={{minHeight: "400px"}}>
                <div id="myGrid" className="ag-theme-alpine rounded-md shadow h-full w-full">
                    <AgGridReact
                        columnMenu={"legacy"}
                        modules={[
                            ClientSideRowModelModule,
                            AllCommunityModule,
                            ColumnMenuModule,
                            ContextMenuModule,
                            ColumnsToolPanelModule,
                            SetFilterModule,
                            ExcelExportModule,
                            CsvExportModule
                        ]}
                        defaultColDef={this.props.defaultColDef}
                        columnDefs={this.props.columnDefs}
                        components={{agDateInput: DTPicker, customNameCellEditor: CustomNameCellEditor}}
                        multiSortKey={"ctrl"}
                        rowData={this.rowData}
                        onGridReady={this.onGridReady}
                        maintainColumnOrder={true} //fixes issue where if you re-order/move column then click anywhere on the grid it reverts this change
                        asyncTransactionWaitMillis={this.props.asyncTransactionWaitMillis}
                        suppressModelUpdateAfterUpdateTransaction={true}
                        getRowId={this.getRowId}
                        onCellEditingStopped={this.onCellEditingStopped}
                        rowSelection={this.props.rowSelection}
                        onSelectionChanged={() => {
                            const selectedRows = this.gridApi.getSelectedRows();
                            if(selectedRows && selectedRows.length > 0){
                                //checks if the setEnableButtons method is null or not
                                this.props.setEnableButtons && this.props.setEnableButtons(true);
                            }
                            else{
                                this.props.setEnableButtons && this.props.setEnableButtons(false);
                            }
                        }}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        onFirstDataRendered={this.onFirstDataRendered.bind(this)}
                        onFilterChanged={(params)=> {
                            onFilterChangedHelper(params, gridFilterStateSessionVariableName, updateCustomerDeploymentHealthGridFilterModelReactive);
                        }}
                        //columnState listeners
                        onSortChanged={this.onColumnStateChanged}
                        onColumnMoved={this.onColumnStateChanged}
                        onColumnVisible={this.onColumnStateChanged}
                        getContextMenuItems={this.getContextMenuItems}
                        sideBar={this.props.sideBar}
                    />
                </div>
            </div>
        );
    }
}
